/**
 * Mauritius Area Data
 * 
 * Comprehensive data for all Mauritius neighbourhoods/areas
 * organized by coastal region. This data is used for area pages
 * in Royale syndication sites.
 */

import type { AreaData, AreaRegionGroup, CoastalRegion } from './area-types';

/**
 * All Mauritius areas organized by region
 */
export const MAURITIUS_REGIONS: AreaRegionGroup[] = [
  {
    region: 'North Coast',
    areas: [
      { slug: 'grand-baie', name: 'Grand Baie' },
      { slug: 'pereybere', name: 'Pereybere' },
      { slug: 'trou-aux-biches', name: 'Trou aux Biches' },
      { slug: 'mont-choisy', name: 'Mont Choisy' },
      { slug: 'balaclava', name: 'Balaclava' },
      { slug: 'cap-malheureux', name: 'Cap Malheureux' },
    ],
  },
  {
    region: 'East Coast',
    areas: [
      { slug: 'belle-mare', name: 'Belle Mare' },
      { slug: 'palmar', name: 'Palmar' },
      { slug: 'trou-deau-douce', name: "Trou d'Eau Douce" },
      { slug: 'poste-lafayette', name: 'Poste Lafayette' },
      { slug: 'roches-noires', name: 'Roches Noires' },
      { slug: 'ile-aux-cerfs', name: 'Ile aux Cerfs' },
    ],
  },
  {
    region: 'South Coast',
    areas: [
      { slug: 'blue-bay', name: 'Blue Bay' },
      { slug: 'mahebourg', name: 'Mahebourg' },
      { slug: 'pointe-desny', name: "Pointe d'Esny" },
      { slug: 'baie-du-cap', name: 'Baie du Cap' },
      { slug: 'riambel', name: 'Riambel' },
      { slug: 'bel-ombre', name: 'Bel Ombre' },
    ],
  },
  {
    region: 'West Coast',
    areas: [
      { slug: 'flic-en-flac', name: 'Flic en Flac' },
      { slug: 'tamarin', name: 'Tamarin' },
      { slug: 'black-river', name: 'Black River' },
      { slug: 'le-morne', name: 'Le Morne' },
      { slug: 'albion', name: 'Albion' },
      { slug: 'cascavelle', name: 'Cascavelle' },
    ],
  },
  {
    region: 'Central Mauritius',
    areas: [
      { slug: 'moka', name: 'Moka' },
      { slug: 'ebene', name: 'Ebène' },
      { slug: 'quatre-bornes', name: 'Quatre Bornes' },
      { slug: 'beau-bassin-rose-hill', name: 'Beau Bassin - Rose Hill' },
      { slug: 'curepipe-forest-side', name: 'Curepipe - Forest Side' },
      { slug: 'vacoas-phoenix', name: 'Vacoas - Phoenix' },
    ],
  },
];

/**
 * Helper to generate slug-to-area lookup
 */
function createAreaLookup(): Record<string, { name: string; region: CoastalRegion }> {
  const lookup: Record<string, { name: string; region: CoastalRegion }> = {};
  for (const regionGroup of MAURITIUS_REGIONS) {
    for (const area of regionGroup.areas) {
      lookup[area.slug] = { name: area.name, region: regionGroup.region };
    }
  }
  return lookup;
}

export const AREA_LOOKUP = createAreaLookup();

/**
 * Comprehensive area data for all Mauritius neighbourhoods
 */
export const MAURITIUS_AREAS: Record<string, AreaData> = {
  // ==========================================
  // NORTH COAST
  // ==========================================
  'grand-baie': {
    slug: 'grand-baie',
    name: 'Grand Baie',
    region: 'North Coast',
    description: "Known as the Riviera of Mauritius, Grand Baie is a cosmopolitan coastal village that perfectly blends luxury living with island charm. From high-end boutiques and international restaurants to pristine azure waters, it attracts discerning buyers seeking vibrant nightlife and yacht-club culture. The area features premium beachfront apartments and villas within walking distance of world-class marinas.",
    extendedDescription: "IRS and PDS schemes offer non-citizens attractive ownership options with strong historical property appreciation. La Croisette shopping mall and Super U provide international retail and daily groceries within minutes. The established expat community creates a welcoming, cosmopolitan atmosphere, whilst proximity to northern islands such as Coin de Mire and Flat Island makes day trips effortless. Buyers choose Grand Baie for its winning combination of investment potential, vibrant lifestyle, and unmatched convenience.",
    coordinates: "20° 00' 50\" S, 57° 34' 56\" E",
    latitude: -20.0139,
    longitude: 57.5822,
    climate: 'Warm & Sheltered',
    priceRange: '$850k - $5M',
    highlights: ['Yacht Club Access', 'Luxury Shopping', 'Nightlife & Dining'],
    heroImage: '',
    seo: {
      metaTitle: 'Grand Baie Properties | Luxury Real Estate in Mauritius',
      metaDescription: 'Discover luxury villas and apartments in Grand Baie, the premier resort destination of Mauritius. Beachfront properties, yacht clubs, and upscale living.',
      keywords: ['Grand Baie real estate', 'Mauritius luxury villas', 'beachfront property Mauritius'],
    },
    faqs: [
      {
        question: 'Why buy property in Grand Baie?',
        answer: 'Grand Baie is Mauritius’s most cosmopolitan coastal hub, combining marina lifestyle, restaurants, shopping and strong resale liquidity. Foreign buyers typically purchase within EDB-approved PDS or related schemes, with price ranges commonly from about $850k to $5M for premium stock.',
      },
      {
        question: 'What is the typical price range in Grand Baie?',
        answer: 'Stone Investment’s Grand Baie guide references a broad band from roughly $850k to $5M depending on beach proximity, scheme, and whether you choose an apartment or villa. Exact pricing varies by development and finish.',
      },
      {
        question: 'Is Grand Baie good for rental investment?',
        answer: 'Yes for many investors. Tourism demand, yacht-club appeal and year-round amenities support holiday lets and longer stays, though yields depend on management quality, seasonality and the specific residence.',
      },
      {
        question: 'Is Grand Baie suitable for families?',
        answer: 'Families value access to schools, medical services and beaches nearby, while still enjoying nightlife and dining. Quieter neighbouring villages such as Pereybère can suit those seeking a calmer base with Grand Baie amenities minutes away.',
      },
    ],
  },
  
  'pereybere': {
    slug: 'pereybere',
    name: 'Pereybere',
    region: 'North Coast',
    description: "Pereybere offers a more intimate beach experience compared to neighbouring Grand Baie, with its calm turquoise lagoon perfect for swimming and snorkelling. This family-friendly area combines authentic Mauritian charm with modern convenience, featuring local restaurants and boutique shops. Properties range from cosy apartments to elegant villas, ideal for those seeking tranquil coastal living.",
    extendedDescription: "Within walking distance of Grand Baie's supermarkets, medical facilities, and restaurants, residents enjoy full convenience without sacrificing peace. Strong rental demand from tourists delivers solid yield potential for buy-to-let investors. The growing expat community adds to the welcoming atmosphere, whilst proximity to Cap Malheureux offers variety for weekend excursions. Buyers are drawn to Pereybere for its balance of tranquillity, accessibility, and excellent investment prospects.",
    coordinates: "19° 59' 28\" S, 57° 35' 42\" E",
    latitude: -19.9911,
    longitude: 57.5950,
    climate: 'Calm & Protected',
    priceRange: '$400k - $2.5M',
    highlights: ['Family-Friendly Beach', 'Snorkelling Paradise', 'Local Charm'],
    heroImage: '',
    seo: {
      metaTitle: 'Pereybere Properties | Beachfront Real Estate Mauritius',
      metaDescription: 'Find your dream property in Pereybere, Mauritius. Family-friendly beaches, crystal-clear lagoons, and authentic island living.',
      keywords: ['Pereybere property', 'Mauritius beach house', 'North Mauritius real estate'],
    },
    faqs: [
      {
        question: 'Why buy in Pereybère?',
        answer: 'Pereybère offers a calmer beach and lagoon setting next to Grand Baie, with family-friendly swimming and a more intimate village scale. Buyers get north-coast convenience without the densest nightlife.',
      },
      {
        question: 'What is the typical price range in Pereybère?',
        answer: 'Guide pricing commonly spans roughly $400k to $2.5M depending on apartment versus villa stock and proximity to the beach. Foreign acquisitions remain subject to approved-scheme rules.',
      },
      {
        question: 'Is Pereybère good for families?',
        answer: 'Yes. Calm waters, walkable amenities and proximity to Grand Baie’s services make it a frequent family shortlist, while still offering tourist rental demand for investors.',
      },
      {
        question: 'How does Pereybère differ from Grand Baie?',
        answer: 'Pereybère is typically quieter and more intimate; Grand Baie is denser with marinas, shopping and nightlife. Many buyers use Pereybère as a tranquil base with Grand Baie minutes away.',
      },
    ],
  },
  
  'trou-aux-biches': {
    slug: 'trou-aux-biches',
    name: 'Trou aux Biches',
    region: 'North Coast',
    description: "Home to one of Mauritius' most beautiful beaches, Trou aux Biches offers pristine white sand and crystal-clear waters stretching over 2 kilometres. This prestigious area hosts five-star resorts and exclusive residential developments, attracting buyers who value serenity over nightlife. Properties here command premium prices for their direct beach access and sunset views.",
    extendedDescription: "The beach is shaded by iconic Casuarina trees, offering natural respite whilst remaining close to Pointe aux Canonniers for additional dining and activity. Renowned diving sites attract underwater enthusiasts, and quiet residential streets appeal to families seeking a safe, peaceful environment. Buyers choose Trou aux Biches for its combination of natural beauty, family-friendly atmosphere, and world-class beachfront living.",
    coordinates: "20° 02' 13\" S, 57° 32' 45\" E",
    latitude: -20.0369,
    longitude: 57.5458,
    climate: 'Pristine & Tranquil',
    priceRange: '$1M - $8M',
    highlights: ['Award-Winning Beach', 'Five-Star Resorts', 'Sunset Views'],
    heroImage: '',
    seo: {
      metaTitle: 'Trou aux Biches Luxury Properties | Premium Mauritius Real Estate',
      metaDescription: 'Exclusive properties in Trou aux Biches, featuring Mauritius finest beach. Luxury villas, resort residences, and beachfront estates.',
      keywords: ['Trou aux Biches luxury', 'Mauritius beachfront villa', 'premium beach property'],
    },
    faqs: [
      {
        question: 'Why buy in Trou aux Biches?',
        answer: 'Trou aux Biches is prized for its long white-sand beach, clear lagoon and five-star resort setting. Buyers seeking serenity and premium beach access often shortlist this north-west coastline.',
      },
      {
        question: 'Is Trou aux Biches more exclusive than Grand Baie?',
        answer: 'It generally feels quieter and more resort-oriented than Grand Baie’s marina buzz. Pricing for direct beach access and premium residences can be elevated accordingly.',
      },
      {
        question: 'What buyers suit Trou aux Biches best?',
        answer: 'Lifestyle buyers prioritising swimming beaches, privacy and resort amenities over nightlife. Investors should assess hotel-competing rental dynamics carefully.',
      },
      {
        question: 'Can foreigners buy freely here?',
        answer: 'Foreign freehold purchases remain limited to approved schemes. Verify each development’s EDB status before offering.',
      },
    ],
  },
  
  'mont-choisy': {
    slug: 'mont-choisy',
    name: 'Mont Choisy',
    region: 'North Coast',
    description: "Mont Choisy represents the pinnacle of Mauritian golf and lifestyle living, centred around the prestigious Mont Choisy Le Golf course. This rapidly developing area offers modern IRS and PDS developments with integrated amenities including championship golf, wellness centres, and beach clubs. Perfect for active retirees and golf enthusiasts seeking a community lifestyle.",
    extendedDescription: "The Mont Choisy Le Golf IRS development delivers integrated golf and beach access within one cohesive community. New infrastructure and road improvements enhance accessibility, whilst proximity to Grand Baie ensures shopping and dining options remain within easy reach. The retirement community thrives on championship courses, beach clubs, and wellness facilities. Buyers are attracted by the blend of active lifestyle, modern amenities, and coastal seclusion.",
    coordinates: "20° 01' 00\" S, 57° 31' 30\" E",
    latitude: -20.0167,
    longitude: 57.5250,
    climate: 'Breezy & Pleasant',
    priceRange: '$600k - $4M',
    highlights: ['Championship Golf', 'IRS/PDS Schemes', 'Modern Developments'],
    heroImage: '',
    seo: {
      metaTitle: 'Mont Choisy Golf Properties | IRS PDS Mauritius',
      metaDescription: 'Invest in Mont Choisy, home to championship golf and luxury IRS/PDS developments. Modern villas, apartments, and integrated resort living.',
      keywords: ['Mont Choisy golf', 'Mauritius IRS property', 'PDS Mauritius investment'],
    },
    faqs: [
      {
        question: 'Why buy in Mont Choisy?',
        answer: 'Mont Choisy is known for golf-led lifestyle living and modern IRS/PDS-style communities with integrated amenities. It attracts active retirees and buyers seeking resort infrastructure with northern beach access.',
      },
      {
        question: 'What property styles dominate Mont Choisy?',
        answer: 'Expect contemporary villas and apartments within gated or resort-integrated developments, often oriented around golf, wellness and beach-club living rather than village streetscapes.',
      },
      {
        question: 'Is Mont Choisy good for golf lifestyle buyers?',
        answer: 'Yes. Championship golf and amenity-rich residences are core to the area’s appeal, with proximity to north-coast beaches and dining adding convenience.',
      },
      {
        question: 'Should foreign buyers verify scheme approval here?',
        answer: 'Always. Confirm EDB approval and title status through a notary before reserving. Stone Investment works with approved developments and independent verification.',
      },
    ],
  },
  
  'balaclava': {
    slug: 'balaclava',
    name: 'Balaclava',
    region: 'North Coast',
    description: "Balaclava is home to some of Mauritius' most exclusive five-star resorts including the Westin Turtle Bay and Maritim, creating a haven of understated luxury. The area's historic ruins add cultural depth to the sophisticated beach club atmosphere. Properties here offer resort-style living with access to world-class spas, restaurants, and private beaches.",
    extendedDescription: "Turtle Bay Marine Park protects vibrant coral reefs right on the doorstep, offering world-class snorkelling and diving. The ruins heritage site adds cultural depth to the sophisticated landscape. Gated communities provide privacy and exclusivity, whilst the area's proximity to Port Louis ensures convenient access to the capital when needed. Buyers choose Balaclava for its combination of natural beauty, heritage, and secure luxury living.",
    coordinates: "20° 03' 45\" S, 57° 30' 50\" E",
    latitude: -20.0625,
    longitude: 57.5139,
    climate: 'Resort Microclimate',
    priceRange: '$1.5M - $10M',
    highlights: ['Five-Star Resorts', 'Historic Heritage', 'Private Beach Clubs'],
    heroImage: '',
    seo: {
      metaTitle: 'Balaclava Luxury Estates | Exclusive Mauritius Properties',
      metaDescription: 'Experience ultra-luxury living in Balaclava. Resort residences, private beach estates, and five-star amenities in Mauritius most exclusive enclave.',
      keywords: ['Balaclava luxury', 'Mauritius resort property', 'exclusive beach estate'],
    },
  },
  
  'cap-malheureux': {
    slug: 'cap-malheureux',
    name: 'Cap Malheureux',
    region: 'North Coast',
    description: "The northernmost point of Mauritius, Cap Malheureux offers dramatic views towards the outer islands and the iconic red-roofed church of Notre Dame Auxiliatrice. This authentic fishing village maintains its Mauritian character while attracting buyers seeking privacy and spectacular ocean panoramas. Properties range from traditional homes to modern villas with unobstructed sea views.",
    coordinates: "19° 58' 50\" S, 57° 36' 50\" E",
    latitude: -19.9806,
    longitude: 57.6139,
    climate: 'Trade Wind Fresh',
    priceRange: '$350k - $2M',
    highlights: ['Iconic Church Views', 'Fishing Village Charm', 'Coin de Mire Views'],
    heroImage: '',
    heroVideo: 'https://res.cloudinary.com/elementary-media/video/upload/f_auto,q_auto,w_1920,br_2000k,vc_h265/cap-malhuereux-video_uvlkxe.mp4',
    seo: {
      metaTitle: 'Cap Malheureux Properties | Authentic Mauritius Living',
      metaDescription: 'Discover Cap Malheureux, Mauritius northernmost village. Authentic charm, spectacular views, and peaceful island living.',
      keywords: ['Cap Malheureux property', 'North Mauritius village', 'sea view property Mauritius'],
    },
  },

  // ==========================================
  // EAST COAST
  // ==========================================
  'belle-mare': {
    slug: 'belle-mare',
    name: 'Belle Mare',
    region: 'East Coast',
    description: "Belle Mare boasts the longest and arguably most beautiful beach on the east coast, with powdery white sand stretching for kilometres. This area hosts prestigious resorts like Constance Belle Mare Plage and The Residence, along with the championship Legend and Links golf courses. Properties offer trade-wind-cooled living with access to world-class golf and watersports.",
    extendedDescription: "IRS and PDS developments offer villas and apartments with strong investment potential, particularly around Constance Belle Mare Plage and Deer Island. The community combines resort refinement with east coast sunrise living—early risers enjoy spectacular dawn views over the Indian Ocean. Residents benefit from the Legend and Links golf courses, watersports centres, and exclusive beach clubs within minutes of home. Buyers choose Belle Mare for its blend of premium resort infrastructure, championship golf, and a lifestyle defined by tropical mornings and trade-wind breezes.",
    coordinates: "20° 11' 30\" S, 57° 46' 30\" E",
    latitude: -20.1917,
    longitude: 57.7750,
    climate: 'Trade Wind Cooled',
    priceRange: '$800k - $6M',
    highlights: ['Championship Golf', 'Endless Beach', 'Resort Living'],
    heroImage: '',
    seo: {
      metaTitle: 'Belle Mare Properties | East Coast Mauritius Real Estate',
      metaDescription: 'Luxury properties in Belle Mare featuring championship golf, pristine beaches, and resort-style amenities on Mauritius east coast.',
      keywords: ['Belle Mare luxury', 'East Mauritius property', 'golf villa Mauritius'],
    },
  },
  
  'palmar': {
    slug: 'palmar',
    name: 'Palmar',
    region: 'East Coast',
    description: "Adjacent to Belle Mare, Palmar offers a quieter alternative while maintaining access to the stunning east coast beaches. The area features the Palmar Beach development and is known for its authentic local feel with small shops and restaurants. Properties here represent excellent value compared to Belle Mare while offering similar beach access.",
    extendedDescription: "As an emerging development area, Palmar presents strong value compared to northern coast hotspots, with villas and apartments often priced 30–40% lower than Belle Mare. Beach bars and waterfront restaurants create a relaxed, convivial atmosphere without the bustle of major resorts. Proximity to Belle Mare means residents enjoy the same beaches and amenities whilst benefiting from lower entry costs. Investors and owner-occupiers alike are drawn to Palmar's rental potential, authentic character, and the opportunity to secure east coast living at a fraction of the premium price.",
    coordinates: "20° 12' 45\" S, 57° 47' 15\" E",
    latitude: -20.2125,
    longitude: 57.7875,
    climate: 'Refreshing Breezes',
    priceRange: '$400k - $2.5M',
    highlights: ['Quiet Beach Life', 'Authentic Character', 'Value Investment'],
    heroImage: '',
    seo: {
      metaTitle: 'Palmar Properties | East Coast Mauritius Investment',
      metaDescription: 'Find value in Palmar, Mauritius east coast. Beautiful beaches, authentic village atmosphere, and excellent investment potential.',
      keywords: ['Palmar Mauritius', 'east coast property', 'beach house Mauritius'],
    },
  },
  
  'trou-deau-douce': {
    slug: 'trou-deau-douce',
    name: "Trou d'Eau Douce",
    region: 'East Coast',
    description: "Famous as the gateway to Ile aux Cerfs, Trou d'Eau Douce is a charming fishing village with colourful boats dotting its lagoon. The area offers authentic Mauritian culture alongside modern developments, with easy access to both east coast beaches and mountain excursions. Properties suit buyers seeking genuine island character with tourism infrastructure.",
    extendedDescription: "The village attracts a mix of local families and expat buyers seeking waterfront living with genuine Mauritian character. Waterfront restaurants and the local market offer fresh produce and Creole cuisine alongside lagoon views. Catamaran tours to Ile aux Cerfs depart from the harbour daily, whilst residents enjoy boat trips, snorkelling, and fishing from their doorstep. Buyers choose Trou d'Eau Douce for its blend of authentic fishing-village life, tourism-driven amenities, and the convenience of being the primary launch point for one of Mauritius's most celebrated island escapes.",
    coordinates: "20° 14' 30\" S, 57° 47' 00\" E",
    latitude: -20.2417,
    longitude: 57.7833,
    climate: 'Lagoon Protected',
    priceRange: '$300k - $1.8M',
    highlights: ['Ile aux Cerfs Gateway', 'Fishing Village Charm', 'Lagoon Living'],
    heroImage: '',
    seo: {
      metaTitle: "Trou d'Eau Douce Properties | Gateway to Ile aux Cerfs",
      metaDescription: "Properties in Trou d'Eau Douce, the charming gateway to Ile aux Cerfs. Fishing village character, lagoon views, and island adventure.",
      keywords: ["Trou d'Eau Douce property", 'Ile aux Cerfs area', 'Mauritius fishing village'],
    },
  },
  
  'poste-lafayette': {
    slug: 'poste-lafayette',
    name: 'Poste Lafayette',
    region: 'East Coast',
    description: "Poste Lafayette offers raw, unspoiled beauty with dramatic coastline and fewer tourists than other east coast areas. The rocky shoreline and powerful waves attract those seeking a more rugged island experience. Properties here are ideal for nature lovers and those wanting authentic Mauritius away from resort developments.",
    extendedDescription: "Nature reserves and hiking trails reward outdoor enthusiasts with endemic wildlife and coastal panoramas. Affordable land plots and eco-conscious developments appeal to buyers seeking to build or retrofit sustainably. The wild coastline offers dramatic views without the crowds found elsewhere on the east coast. Buyers choose Poste Lafayette for its combination of lower entry prices, unspoiled landscapes, and the opportunity to create a bespoke home in one of Mauritius's most authentic coastal environments.",
    coordinates: "20° 07' 00\" S, 57° 45' 30\" E",
    latitude: -20.1167,
    longitude: 57.7583,
    climate: 'Wild & Windswept',
    priceRange: '$250k - $1.2M',
    highlights: ['Unspoiled Nature', 'Dramatic Coastline', 'Authentic Mauritius'],
    heroImage: '',
    seo: {
      metaTitle: 'Poste Lafayette Properties | Unspoiled East Coast Mauritius',
      metaDescription: 'Discover Poste Lafayette, where dramatic coastlines meet authentic Mauritian living. Properties for nature lovers and adventure seekers.',
      keywords: ['Poste Lafayette', 'raw Mauritius', 'coastal property east'],
    },
  },
  
  'roches-noires': {
    slug: 'roches-noires',
    name: 'Roches Noires',
    region: 'East Coast',
    description: "Named for its distinctive black volcanic rocks, Roches Noires is a surfer's paradise with consistent swells and a laid-back atmosphere. This area attracts water sports enthusiasts and those seeking alternative lifestyle properties away from mainstream tourism. The village maintains strong local character with affordable real estate options.",
    extendedDescription: "The surf breaks attract a younger, creative community of artists and weekend retreat-seekers who value authenticity over luxury branding. Proximity to Poste Lafayette provides access to hiking and nature reserves whilst keeping Roches Noires firmly on the map for water sports. Properties range from surf shacks to renovated cottages with strong rental appeal during peak season. Growing interest from younger buyers and second-home seekers makes Roches Noires one of the east coast's most promising value investments for those who prize lifestyle over prestige.",
    coordinates: "20° 06' 15\" S, 57° 44' 00\" E",
    latitude: -20.1042,
    longitude: 57.7333,
    climate: 'Surfer Breezy',
    priceRange: '$200k - $900k',
    highlights: ["Surfer's Paradise", 'Black Rock Beaches', 'Alternative Lifestyle'],
    heroImage: '',
    seo: {
      metaTitle: 'Roches Noires Properties | Surf & Alternative Living Mauritius',
      metaDescription: 'Properties in Roches Noires for surfers and free spirits. Black volcanic rocks, consistent waves, and affordable island living.',
      keywords: ['Roches Noires surf', 'Mauritius surf property', 'east coast alternative'],
    },
  },
  
  'ile-aux-cerfs': {
    slug: 'ile-aux-cerfs',
    name: 'Ile aux Cerfs',
    region: 'East Coast',
    description: "The legendary Ile aux Cerfs is a private island paradise renowned for its pristine beaches, championship golf course designed by Bernhard Langer, and exclusive day-trip destination status. While residential properties aren't available on the island itself, nearby developments offer views and boat access to this tropical jewel. The golf course is considered one of the world's most beautiful.",
    extendedDescription: "Mainland properties in Trou d'Eau Douce and Belle Mare offer boat access to the island, combining residential living with daily visits to its white-sand beaches and Bernhard Langer-designed golf course. Tourism-driven rental income is a key draw for investors, as Ile aux Cerfs receives thousands of visitors annually. Properties with marina berths or private launch access command premium prices. Buyers choose the Ile aux Cerfs catchment for its unique combination of island-paradise access, world-class golf, and strong holiday-let demand that supports investment returns.",
    coordinates: "20° 16' 00\" S, 57° 48' 30\" E",
    latitude: -20.2667,
    longitude: 57.8083,
    climate: 'Island Paradise',
    priceRange: 'Nearby: $500k - $3M',
    highlights: ['World-Class Golf', 'Island Day Trips', 'Pristine Beaches'],
    heroImage: '',
    seo: {
      metaTitle: 'Near Ile aux Cerfs Properties | Island Access Mauritius',
      metaDescription: 'Properties with access to Ile aux Cerfs, Mauritius legendary private island. Golf, beaches, and paradise living nearby.',
      keywords: ['Ile aux Cerfs property', 'Mauritius island golf', 'paradise beach access'],
    },
  },

  // ==========================================
  // SOUTH COAST
  // ==========================================
  'blue-bay': {
    slug: 'blue-bay',
    name: 'Blue Bay',
    region: 'South Coast',
    description: "Blue Bay is home to a protected marine park with crystal-clear waters revealing vibrant coral reefs teeming with tropical fish. The area's natural lagoon is considered the best for glass-bottom boat excursions and snorkelling in Mauritius. Properties here offer direct access to this underwater paradise, attracting eco-conscious buyers and diving enthusiasts.",
    coordinates: "20° 26' 45\" S, 57° 42' 00\" E",
    latitude: -20.4458,
    longitude: 57.7000,
    climate: 'Protected Lagoon',
    priceRange: '$350k - $2M',
    highlights: ['Marine Park Access', 'Snorkelling Paradise', 'Glass-Bottom Boats'],
    heroImage: '',
    seo: {
      metaTitle: 'Blue Bay Properties | Marine Park Living Mauritius',
      metaDescription: 'Properties in Blue Bay with access to Mauritius only marine park. Snorkelling, diving, and eco-friendly tropical living.',
      keywords: ['Blue Bay marine park', 'Mauritius snorkelling property', 'south coast beach'],
    },
  },
  
  'mahebourg': {
    slug: 'mahebourg',
    name: 'Mahebourg',
    region: 'South Coast',
    description: "Steeped in history, Mahebourg was the original French colonial capital and site of the famous 1810 naval battle. Today this charming town offers authentic Mauritian life with its Monday market, waterfront promenade, and views across the bay to Ile aux Aigrettes. Properties here provide historical character and genuine cultural immersion at accessible prices.",
    coordinates: "20° 24' 30\" S, 57° 42' 15\" E",
    latitude: -20.4083,
    longitude: 57.7042,
    climate: 'Historic Bay',
    priceRange: '$200k - $1M',
    highlights: ['Colonial History', 'Monday Market', 'Bay Panoramas'],
    heroImage: '',
    seo: {
      metaTitle: 'Mahebourg Properties | Historic South Mauritius',
      metaDescription: 'Properties in historic Mahebourg, the former colonial capital. Waterfront living, cultural heritage, and authentic Mauritius.',
      keywords: ['Mahebourg property', 'historic Mauritius', 'south coast investment'],
    },
  },
  
  'pointe-desny': {
    slug: 'pointe-desny',
    name: "Pointe d'Esny",
    region: 'South Coast',
    description: "Pointe d'Esny offers a long sandy beach with calm lagoon waters, providing one of the best swimming spots in the south. The area is less developed than northern beaches, offering a more exclusive and private beach experience. Properties here range from beach cottages to luxury villas, all enjoying the unspoiled natural surroundings.",
    extendedDescription: "From modest beach cottages to premium villas, Pointe d'Esny attracts buyers seeking a quiet residential character away from the bustle of Grand Baie or Mahebourg. Le Chaland beach and the nearby Île aux Aigrettes nature reserve offer sailing, kayaking, and wildlife spotting within reach. The sailing community thrives here, with calm lagoon waters ideal for catamarans and dinghies. Buyers choose Pointe d'Esny for its peaceful neighbourhood feel, proximity to Blue Bay's marine park, and the privilege of living beside one of the south coast's finest swimming beaches.",
    coordinates: "20° 25' 45\" S, 57° 44' 30\" E",
    latitude: -20.4292,
    longitude: 57.7417,
    climate: 'Calm Lagoon',
    priceRange: '$450k - $3M',
    highlights: ['Private Beach', 'Calm Lagoon', 'Exclusive Setting'],
    heroImage: '',
    seo: {
      metaTitle: "Pointe d'Esny Properties | Exclusive South Beach Mauritius",
      metaDescription: "Exclusive properties at Pointe d'Esny with private beach access. Calm lagoon, unspoiled nature, and south coast luxury.",
      keywords: ["Pointe d'Esny luxury", 'private beach Mauritius', 'south coast exclusive'],
    },
  },
  
  'baie-du-cap': {
    slug: 'baie-du-cap',
    name: 'Baie du Cap',
    region: 'South Coast',
    description: "Baie du Cap is one of the most scenic coastal villages in southern Mauritius, known for its dramatic ocean views, winding coastal road and natural landscapes between sea, cliffs and mountains. Located between Le Morne and Bel Ombre, the area offers a peaceful south coast lifestyle shaped by open views, quiet beaches, local village life and access to some of the island's most striking natural scenery.",
    extendedDescription: "Properties appeal to buyers looking for privacy, authenticity and a slower rhythm away from the busier residential and resort regions. Baie du Cap benefits from proximity to Macondé Viewpoint, Le Morne, Bel Ombre, Chamarel and the Black River Gorges region. Residents can access hiking, coastal drives, nature outings, lagoon activities and nearby resort amenities while remaining in a low-density environment. The area is less commercial than the west and north coast, but this is precisely what gives Baie du Cap its quiet charm and long-term lifestyle appeal. Buyers choose Baie du Cap for its natural beauty, peaceful south coast character and access to one of the most scenic coastal stretches in Mauritius.",
    coordinates: "20° 29' 00\" S, 57° 22' 00\" E",
    latitude: -20.4833,
    longitude: 57.3667,
    climate: 'Scenic Coastal',
    priceRange: '$200k - $1.5M',
    highlights: ['Scenic Coastal Road', 'Peaceful Village Living', 'Nature Access'],
    heroImage: '',
    seo: {
      metaTitle: 'Baie du Cap Properties | Scenic South Coast Mauritius',
      metaDescription: 'Properties in Baie du Cap, one of the most scenic coastal villages in southern Mauritius. Dramatic ocean views, peaceful village living and nature access.',
      keywords: ['Baie du Cap property', 'south coast Mauritius', 'scenic coastal village'],
    },
  },
  
  'riambel': {
    slug: 'riambel',
    name: 'Riambel',
    region: 'South Coast',
    description: "Riambel is a quiet coastal village on the south coast of Mauritius, known for its long, open shoreline, peaceful atmosphere and more natural rhythm of island living. Located near Surinam and Souillac, the area offers a low-density lifestyle shaped by sea views, open coastal roads, local neighbourhoods and access to the wilder landscapes of the south.",
    extendedDescription: "Properties appeal to buyers looking for privacy, space and a slower residential setting away from the busier tourism regions. Riambel benefits from proximity to Gris Gris, Rochester Falls, Souillac, Saint Félix and Bel Ombre, giving residents access to dramatic coastal scenery, nature-based outings and essential daily amenities within a short drive. The area remains more local and understated than the resort zones nearby, with a lifestyle defined by quiet beaches, south coast breezes and a strong sense of space. Buyers considering Riambel are often drawn to its simplicity, natural setting and potential as a peaceful residential or second-home location.",
    coordinates: "20° 30' 54\" S, 57° 30' 00\" E",
    latitude: -20.5150,
    longitude: 57.5000,
    climate: 'South Coast Breeze',
    priceRange: '$180k - $1.2M',
    highlights: ['Quiet Coastline', 'Natural Living', 'South Coast Access'],
    heroImage: '',
    seo: {
      metaTitle: 'Riambel Properties | Quiet South Coast Mauritius',
      metaDescription: 'Properties in Riambel, a quiet coastal village on the south coast of Mauritius. Open shoreline, peaceful atmosphere and natural island living.',
      keywords: ['Riambel property', 'south coast Mauritius', 'quiet coastal village'],
    },
  },
  
  'bel-ombre': {
    slug: 'bel-ombre',
    name: 'Bel Ombre',
    region: 'South Coast',
    description: "Bel Ombre represents one of Mauritius' most ambitious integrated resort developments, set on a former sugar estate with a world-class golf course. The Heritage Resorts complex offers outstanding IRS villas and apartments with access to nature reserves, spa facilities, and multiple restaurants. The area's dramatic mountain backdrop and wild southern coastline create a unique luxury proposition.",
    extendedDescription: "Heritage Resorts IRS delivers integrated ownership within a converted sugar estate, with villas and apartments offering access to the Frederica Nature Reserve and the resort's championship golf course. The C Beach Club provides a refined beachfront experience, whilst the estate's colonial sugar mill heritage adds historical character to the development. The south coast's wilder, less developed coastline appeals to buyers seeking a secluded luxury escape with nature on the doorstep. Buyers choose Bel Ombre for the blend of IRS benefits, wilderness access, and a lifestyle rooted in Mauritius's plantation heritage.",
    coordinates: "20° 29' 30\" S, 57° 24' 00\" E",
    latitude: -20.4917,
    longitude: 57.4000,
    climate: 'Mountain Fresh',
    priceRange: '$1M - $8M',
    highlights: ['Heritage Estate', 'Championship Golf', 'Nature Reserve'],
    heroImage: '',
    seo: {
      metaTitle: 'Bel Ombre Luxury Properties | Heritage Estate Mauritius',
      metaDescription: 'Luxury IRS properties in Bel Ombre Heritage estate. Championship golf, nature reserves, and south coast grandeur.',
      keywords: ['Bel Ombre IRS', 'Heritage estate property', 'luxury golf Mauritius'],
    },
    faqs: [
      {
        question: 'Why buy in Bel Ombre?',
        answer: 'Bel Ombre anchors ambitious integrated resort living on Mauritius’s south coast, with golf, nature reserves and Heritage-linked amenities set against a dramatic mountain and ocean backdrop.',
      },
      {
        question: 'Is Bel Ombre good for luxury villa buyers?',
        answer: 'Yes. IRS/PDS-style villas and apartments in estate settings are a hallmark of the area, appealing to buyers seeking resort services with more wilderness character than the north.',
      },
      {
        question: 'How does the south coast differ from the north?',
        answer: 'The south is wilder and less densely developed, with stronger nature and golf positioning. The north offers denser amenities and often deeper short-term rental liquidity in village hubs.',
      },
      {
        question: 'What should investors check in Bel Ombre?',
        answer: 'Scheme approval, estate service charges, rental management options and personal-use plans. Stone Investment can compare Bel Ombre stock against west- and north-coast alternatives.',
      },
    ],
  },

  // ==========================================
  // WEST COAST
  // ==========================================
  'flic-en-flac': {
    slug: 'flic-en-flac',
    name: 'Flic en Flac',
    region: 'West Coast',
    description: "Flic en Flac features the longest public beach on the west coast, stretching over 8 kilometres of white sand with spectacular sunsets. This vibrant town offers a mix of local life and tourism, with restaurants, bars, and shops lining the beachfront. Properties range from affordable apartments to luxury villas, all benefiting from the area's protected dry climate.",
    extendedDescription: "A diverse mix of apartments and villas caters to both investors and owner-occupiers, with strong rental demand and appreciation potential driven by the year-round dry climate. Casela Nature Park lies just inland, offering safari experiences and wildlife encounters, whilst Cathedral reef draws divers to explore pristine underwater terrain. The growing expat community creates a welcoming international atmosphere with excellent dining and leisure options. Buyers choose Flic en Flac for its combination of beach lifestyle, proximity to nature attractions, and solid investment returns.",
    coordinates: "20° 17' 00\" S, 57° 22' 00\" E",
    latitude: -20.2833,
    longitude: 57.3667,
    climate: 'Dry & Sunny',
    priceRange: '$300k - $3M',
    highlights: ['Longest Beach', 'Spectacular Sunsets', 'Vibrant Lifestyle'],
    heroImage: '',
    seo: {
      metaTitle: 'Flic en Flac Properties | West Coast Mauritius',
      metaDescription: 'Properties in Flic en Flac with the longest beach in Mauritius. Sunset views, vibrant lifestyle, and west coast living.',
      keywords: ['Flic en Flac property', 'west coast beach', 'sunset Mauritius'],
    },
    faqs: [
      {
        question: 'Why buy property in Flic en Flac?',
        answer: 'Flic en Flac offers one of the longest public beaches on the west coast, with restaurants, shops and a mix of local and tourist life. It appeals to buyers seeking beach access with a livelier village feel.',
      },
      {
        question: 'What is the typical buyer profile in Flic en Flac?',
        answer: 'The market attracts lifestyle buyers and investors across a wider price ladder than ultra-prime north-coast pockets, from apartments to luxury villas benefiting from the protected dry west-coast climate.',
      },
      {
        question: 'Is Flic en Flac good for holiday rentals?',
        answer: 'Beach length and tourism infrastructure support short-let demand, especially in well-managed residences near the lagoon. Performance still depends on unit quality, seasonality and professional management.',
      },
      {
        question: 'How close is Flic en Flac to other west-coast hubs?',
        answer: 'It sits within easy reach of Tamarin and Black River, making it practical for buyers who want beachfront energy while exploring neighbouring villages for dining and outdoor activities.',
      },
    ],
  },
  
  'tamarin': {
    slug: 'tamarin',
    name: 'Tamarin',
    region: 'West Coast',
    description: "Tamarin is the surf capital of Mauritius, attracting a bohemian-chic crowd of water sports enthusiasts and artists. The area offers dolphin-watching excursions, a salt pan heritage, and dramatic mountain views. Properties range from surf shacks to luxury hillside villas, creating a unique blend of alternative lifestyle and upscale development including the Tamarina Golf & Spa development.",
    extendedDescription: "The Tamarina Golf & Spa IRS presents premium villas and apartments with championship golf and marina access, appealing to investors seeking IRS benefits. The historic salt pans add cultural character to a community known for its bohemian-chic lifestyle against the backdrop of Rivière Noire mountains. Dolphin excursions depart from Tamarin Bay, offering residents and visitors unforgettable marine encounters. Buyers are drawn to Tamarin for its unique blend of upscale resort infrastructure and laid-back creative community.",
    coordinates: "20° 19' 30\" S, 57° 22' 30\" E",
    latitude: -20.3250,
    longitude: 57.3750,
    climate: 'Surfer Perfect',
    priceRange: '$400k - $4M',
    highlights: ['Surf Capital', 'Dolphin Watching', 'Mountain Backdrop'],
    heroImage: '',
    seo: {
      metaTitle: 'Tamarin Properties | Surf & Golf Mauritius',
      metaDescription: 'Properties in Tamarin, Mauritius surf capital. Bohemian lifestyle, dolphin watching, and luxury golf developments.',
      keywords: ['Tamarin surf property', 'Mauritius dolphin area', 'west coast lifestyle'],
    },
    faqs: [
      {
        question: 'Why buy property in Tamarin?',
        answer: 'Tamarin is Mauritius’s west-coast surf capital, known for dolphin watching, dramatic mountains and a creative, outdoor lifestyle. It attracts buyers seeking sunsets, space and a less formal atmosphere than the north coast.',
      },
      {
        question: 'What is the typical price range in Tamarin?',
        answer: 'West-coast pricing varies widely from lifestyle villas to golf-estate stock around Tamarina. Expect a broad spectrum from mid-market apartments to multi-million-dollar hillside and beach-proximate homes.',
      },
      {
        question: 'Is Tamarin good for families and surf lifestyles?',
        answer: 'Yes. The area suits active families and water-sports enthusiasts, with village amenities and proximity to Black River Gorges for hiking. International buyers should still purchase within approved schemes where required.',
      },
      {
        question: 'How does Tamarin compare with Grand Baie for investment?',
        answer: 'Grand Baie offers denser amenities and often deeper resale liquidity; Tamarin appeals for climate, sunsets and outdoor living. Many investors choose based on personal use first, then rental seasonality.',
      },
    ],
  },
  
  'black-river': {
    slug: 'black-river',
    name: 'Black River',
    region: 'West Coast',
    description: "Black River serves as the gateway to the stunning Black River Gorges National Park, offering outdoor enthusiasts hiking, waterfalls, and endemic wildlife. The area combines coastal living with mountain accessibility, featuring marinas and the departure point for big-game fishing. Properties suit nature lovers seeking the best of both coastal and highland Mauritius.",
    extendedDescription: "River valley properties and coastal homes offer diverse investment options, with nature tourism driving strong rental demand from hikers and ecotourists. Black River Gorges delivers world-class trekking trails and endemic species encounters, whilst Chamarel's Seven Coloured Earths lie within easy reach for day excursions. Deep-sea fishing charters depart from Black River marinas, attracting anglers from around the globe. Buyers choose Black River for its unique positioning between mountain wilderness and coastal living, plus the area's growing reputation as a nature tourism hub.",
    coordinates: "20° 21' 00\" S, 57° 22' 00\" E",
    latitude: -20.3500,
    longitude: 57.3667,
    climate: 'Coastal Gateway',
    priceRange: '$350k - $2.5M',
    highlights: ['National Park Access', 'Big Game Fishing', 'Marina Lifestyle'],
    heroImage: '',
    seo: {
      metaTitle: 'Black River Properties | Nature Gateway Mauritius',
      metaDescription: 'Properties in Black River near the gorges national park. Hiking, fishing, marina living, and nature immersion.',
      keywords: ['Black River property', 'Mauritius national park', 'fishing marina'],
    },
    faqs: [
      {
        question: 'Why buy in Black River (Rivière Noire)?',
        answer: 'Black River combines coastal living with gateway access to Black River Gorges National Park, marinas and big-game fishing. It suits nature-oriented buyers who want west-coast light and outdoor adventure.',
      },
      {
        question: 'What property types are common in Black River?',
        answer: 'Inventory ranges from marina-adjacent apartments and villas to hillside homes with mountain or ocean outlooks. Foreign buyers should focus on EDB-approved developments when acquiring freehold.',
      },
      {
        question: 'Is Black River good for nature lovers?',
        answer: 'Yes. Proximity to national park trails, waterfalls and west-coast beaches makes it one of Mauritius’s strongest outdoor lifestyle bases, while still remaining connected to Tamarin and Flic en Flac amenities.',
      },
      {
        question: 'Who is Black River best for?',
        answer: 'It suits buyers prioritising outdoor living, privacy and west-coast sunsets over dense nightlife. Investors should assess rental depth against competing west-coast hubs.',
      },
    ],
  },
  
  'le-morne': {
    slug: 'le-morne',
    name: 'Le Morne',
    region: 'West Coast',
    description: "Dominated by the UNESCO World Heritage-listed Le Morne Brabant mountain, this area offers some of Mauritius' most dramatic scenery. The peninsula is world-renowned for kitesurfing, with consistent trade winds and flat lagoon conditions. Luxury resorts dot the coastline, and properties here command premium prices for their iconic mountain views and exclusive beach access.",
    extendedDescription: "The UNESCO heritage trail up Le Morne Brabant offers challenging mountain hiking and profound cultural significance as a symbol of slavery abolition. One Eye ranks among the world's premier kitesurfing spots, with flat lagoon waters and consistent winds attracting enthusiasts year-round. Dinarobin and Paradis resorts anchor the peninsula's luxury hospitality, creating spill-over demand for nearby residences. Buyers choose Le Morne for its iconic landscape, world-class watersports, and the prestige of owning property in a UNESCO World Heritage setting.",
    coordinates: "20° 27' 00\" S, 57° 18' 30\" E",
    latitude: -20.4500,
    longitude: 57.3083,
    climate: 'Kite Paradise',
    priceRange: '$800k - $6M',
    highlights: ['UNESCO Heritage', 'Kitesurfing Mecca', 'Iconic Mountain'],
    heroImage: '',
    seo: {
      metaTitle: 'Le Morne Properties | UNESCO Heritage Mauritius',
      metaDescription: 'Exclusive properties at Le Morne, a UNESCO World Heritage site. Iconic mountain views, kitesurfing, and luxury living.',
      keywords: ['Le Morne luxury', 'UNESCO property Mauritius', 'kitesurfing real estate'],
    },
    faqs: [
      {
        question: 'Why buy near Le Morne?',
        answer: 'Le Morne is defined by the UNESCO-listed mountain, kitesurf lagoons and exclusive coastal scenery. It suits buyers seeking iconic views and a high-end south-west lifestyle.',
      },
      {
        question: 'Is Le Morne suitable for water-sports lifestyles?',
        answer: 'Yes. Consistent winds and lagoon conditions make it a global kitesurf destination, with luxury resorts reinforcing the area’s premium positioning.',
      },
      {
        question: 'What property types are available around Le Morne?',
        answer: 'Stock is more selective than in dense village markets, focusing on resort-linked and exclusive coastal residences. Foreign buyers must still use approved acquisition routes.',
      },
      {
        question: 'Who should shortlist Le Morne?',
        answer: 'Buyers prioritising dramatic landscape, water sports and privacy. Those needing dense daily amenities may prefer Grand Baie, Tamarin or Flic en Flac as a primary base.',
      },
    ],
  },
  
  'albion': {
    slug: 'albion',
    name: 'Albion',
    region: 'West Coast',
    description: "Albion offers rugged coastal beauty with dramatic cliffs and a famous lighthouse guiding ships through the reef passage. Less developed than Flic en Flac, the area provides authentic west coast living with excellent value properties. The lighthouse walk and sunset views attract photographers and nature enthusiasts, while the area's IRS developments bring modern luxury.",
    extendedDescription: "Albion lighthouse walks along the dramatic cliffs provide memorable coastal experiences, whilst Club Med sits nearby for residents seeking resort amenities without the premium price tag. Emerging IRS developments offer modern villas and apartments at more accessible entry points than established west coast hotspots. Proximity to Flic en Flac ensures beaches, restaurants, and shops remain within a short drive. Buyers choose Albion for its authentic west coast character, coastal cliff walks, and strong value proposition with growing infrastructure.",
    coordinates: "20° 13' 00\" S, 57° 24' 00\" E",
    latitude: -20.2167,
    longitude: 57.4000,
    climate: 'Cliff Fresh',
    priceRange: '$300k - $2M',
    highlights: ['Historic Lighthouse', 'Dramatic Cliffs', 'Sunset Photography'],
    heroImage: '',
    seo: {
      metaTitle: 'Albion Properties | Coastal Cliffs Mauritius',
      metaDescription: 'Properties in Albion with lighthouse views and dramatic cliffs. Authentic west coast, sunset walks, and value investment.',
      keywords: ['Albion property', 'lighthouse Mauritius', 'west coast cliffs'],
    },
  },
  
  'cascavelle': {
    slug: 'cascavelle',
    name: 'Cascavelle',
    region: 'West Coast',
    description: "Cascavelle is a rapidly developing inland area centred around the Cascavelle Shopping Mall, offering modern amenities with easy access to both west coast beaches and central Mauritius. The area features new residential developments catering to families seeking value and convenience. Properties here offer larger land plots and modern infrastructure at accessible prices.",
    extendedDescription: "Cascavelle Shopping Mall delivers supermarkets, retail, and dining within walking distance, whilst Flic en Flac's beaches are just fifteen minutes away by car. New residential estates offer family-oriented communities with modern amenities and larger plots at competitive prices. The central location provides quick access to Port Louis, Tamarin, and coastal villages, making daily commutes and weekend excursions straightforward. Buyers choose Cascavelle for its combination of modern convenience, family-friendly environment, and excellent value within the west coast catchment.",
    coordinates: "20° 18' 30\" S, 57° 25' 30\" E",
    latitude: -20.3083,
    longitude: 57.4250,
    climate: 'Plateau Pleasant',
    priceRange: '$200k - $1.2M',
    highlights: ['Shopping Hub', 'Family Living', 'Modern Developments'],
    heroImage: '',
    seo: {
      metaTitle: 'Cascavelle Properties | Modern Living Mauritius',
      metaDescription: 'Modern properties in Cascavelle with shopping and amenities. Family-friendly developments and west coast accessibility.',
      keywords: ['Cascavelle property', 'Mauritius family home', 'modern development'],
    },
  },

  // ==========================================
  // CENTRAL MAURITIUS
  // ==========================================
  'moka': {
    slug: 'moka',
    name: 'Moka',
    region: 'Central Mauritius',
    description: "Moka is one of the most established central regions in Mauritius, combining cooler inland living with strong access to schools, offices, healthcare and daily amenities. Set against the Moka mountain range, the region has evolved into one of the island's most organised live-work environments.",
    extendedDescription: "Properties offer practical central living with access to business parks, shopping centres, sports facilities, medical services and some of Mauritius' leading educational institutions. Moka Smart City, Telfair, Helvétia, Bagatelle and Vivéa form the backbone of the area's modern growth. Residents benefit from proximity to Le Bocage International School, Lycée des Mascareignes, Clavis International Primary School, the University of Mauritius, Wellkin Hospital, Bagatelle Mall and Tribeca. The region combines central convenience with a more structured, green and residential atmosphere than the denser urban corridors nearby. Buyers choose Moka for its central location, cooler climate, school access and long-term appeal as one of Mauritius' most complete residential, business and education hubs.",
    coordinates: "20° 13' 00\" S, 57° 29' 00\" E",
    latitude: -20.2167,
    longitude: 57.4833,
    climate: 'Cooler Inland',
    priceRange: '$250k - $1.5M',
    highlights: ['Central Living', 'Education Hub', 'Smart City Lifestyle'],
    heroImage: '',
    seo: {
      metaTitle: 'Moka Properties | Central Mauritius Real Estate',
      metaDescription: 'Properties in Moka, one of the most established central regions in Mauritius. Smart city living, education hub and cooler inland climate.',
      keywords: ['Moka property', 'central Mauritius real estate', 'Moka Smart City'],
    },
    faqs: [
      {
        question: 'Why buy property in Moka?',
        answer: 'Moka and the Beau Plan / Smart City ecosystem appeal to buyers seeking modern master-planned living, schools and central island convenience rather than a pure beachfront lifestyle.',
      },
      {
        question: 'Is Moka suitable for foreign buyers?',
        answer: 'Foreign buyers typically acquire within approved Smart City or related frameworks rather than unrestricted land. Stone Investment can confirm scheme eligibility for specific developments.',
      },
      {
        question: 'Who is Moka best for?',
        answer: 'It suits families and professionals wanting integrated amenities, contemporary residences and easier island-wide access, including those who value community infrastructure over coastal nightlife.',
      },
      {
        question: 'Does Moka offer rental potential?',
        answer: 'Corporate and long-stay demand can support rentals in well-located Smart City stock, though the profile differs from coastal holiday lets. Underwrite each project on its tenant mix and management model.',
      },
    ],
  },

  'ebene': {
    slug: 'ebene',
    name: 'Ebène',
    region: 'Central Mauritius',
    description: "Ebène is Mauritius' main business and technology district, known for its corporate towers, ICT infrastructure and direct access to the island's central road network. Located between Quatre Bornes, Rose Hill and Moka, the area has become one of the most important commercial hubs in Mauritius.",
    extendedDescription: "Properties in and around Ebène appeal to professionals, investors and residents who want fast access to offices, services, transport routes and central amenities. Ebène Cybercity is home to major financial, technology, outsourcing, legal and corporate service companies, making it a natural choice for those working in Mauritius' modern economy. Residents benefit from proximity to Tribeca, Bagatelle, Trianon, Rose Hill, Quatre Bornes and Moka, while schools, clinics, shopping centres and restaurants are all within a short drive. The region is less coastal and more urban, but its convenience and employment base make it one of the most practical central locations on the island. Buyers choose Ebène for its business access, rental demand, central connectivity and strong appeal to professionals seeking a convenient urban lifestyle close to Mauritius' main commercial corridors.",
    coordinates: "20° 14' 30\" S, 57° 29' 30\" E",
    latitude: -20.2417,
    longitude: 57.4917,
    climate: 'Urban Central',
    priceRange: '$180k - $900k',
    highlights: ['Business District', 'Central Connectivity', 'Professional Rental Demand'],
    heroImage: '',
    seo: {
      metaTitle: 'Ebène Properties | Business District Mauritius',
      metaDescription: 'Properties in Ebène, Mauritius main business and technology district. Corporate hub, central connectivity and strong rental demand.',
      keywords: ['Ebène property', 'Cybercity Mauritius', 'business district real estate'],
    },
  },

  'quatre-bornes': {
    slug: 'quatre-bornes',
    name: 'Quatre Bornes',
    region: 'Central Mauritius',
    description: "Quatre Bornes is one of Mauritius' most practical central towns, known for its convenient location, residential neighbourhoods, shopping streets and easy access to Ebène, Rose Hill, Moka and Phoenix. Often referred to as La Ville des Fleurs, the region offers a balanced central lifestyle with established homes, apartment developments, schools, offices, markets and daily services.",
    extendedDescription: "Properties appeal to residents who prioritise connectivity, affordability and access to the island's main urban and business corridors. Quatre Bornes benefits from proximity to Ebène Cybercity, Trianon, Phoenix, Rose Hill and Moka, making it especially convenient for professionals and families. The town is also known for its market, retail activity, medical services and transport access, with the metro and main road network improving movement across the centre of the island. While less resort-oriented than coastal regions, Quatre Bornes offers strong everyday functionality and remains one of the most established residential centres in Mauritius. Buyers choose Quatre Bornes for its central position, practical lifestyle, transport access and strong appeal as a residential base close to Mauritius' main business and education hubs.",
    coordinates: "20° 15' 45\" S, 57° 28' 45\" E",
    latitude: -20.2625,
    longitude: 57.4792,
    climate: 'Central Temperate',
    priceRange: '$150k - $750k',
    highlights: ['Central Residential', 'Market Town', 'Strong Connectivity'],
    heroImage: '',
    seo: {
      metaTitle: 'Quatre Bornes Properties | Central Mauritius Living',
      metaDescription: 'Properties in Quatre Bornes, one of Mauritius most practical central towns. Residential neighbourhoods, market town and strong connectivity.',
      keywords: ['Quatre Bornes property', 'central Mauritius', 'residential real estate'],
    },
  },

  'beau-bassin-rose-hill': {
    slug: 'beau-bassin-rose-hill',
    name: 'Beau Bassin - Rose Hill',
    region: 'Central Mauritius',
    description: "Beau Bassin - Rose Hill is one of Mauritius' most established central urban areas, offering a practical residential lifestyle with strong access to schools, offices, transport links and everyday amenities. Located between Quatre Bornes, Ebène, Moka and Port Louis, the region combines historic neighbourhoods, apartment living, family homes and active town-centre convenience.",
    extendedDescription: "Properties appeal to residents who want central access without being directly inside the capital or the island's busiest business districts. Beau Bassin - Rose Hill benefits from the Metro Express, key road connections, local shops, supermarkets, schools, clinics, cafés and cultural facilities. Its location makes it especially convenient for professionals working in Ebène, Port Louis or Moka, while families value its established residential pockets and proximity to several central schools. The area offers a more urban, lived-in atmosphere than newer smart city districts, with strong day-to-day functionality and excellent islandwide connectivity. Buyers choose Beau Bassin - Rose Hill for its central position, transport access, established neighbourhoods and practical lifestyle close to Mauritius' main business, education and service hubs.",
    coordinates: "20° 14' 30\" S, 57° 28' 00\" E",
    latitude: -20.2417,
    longitude: 57.4667,
    climate: 'Central Urban',
    priceRange: '$140k - $700k',
    highlights: ['Established Neighbourhoods', 'Metro Access', 'Central Convenience'],
    heroImage: '',
    seo: {
      metaTitle: 'Beau Bassin - Rose Hill Properties | Central Mauritius',
      metaDescription: 'Properties in Beau Bassin - Rose Hill, one of Mauritius most established central urban areas. Metro access, established neighbourhoods and central convenience.',
      keywords: ['Beau Bassin property', 'Rose Hill real estate', 'central Mauritius living'],
    },
  },

  'curepipe-forest-side': {
    slug: 'curepipe-forest-side',
    name: 'Curepipe - Forest Side',
    region: 'Central Mauritius',
    description: "Curepipe - Forest Side is one of the coolest and most established residential areas in Mauritius, known for its elevated climate, historic neighbourhoods and strong access to schools, clinics, shops and central services. Located in the upper Plaines Wilhems region, the area offers a quieter inland lifestyle with traditional family homes, apartment buildings, gardens and long-standing residential communities.",
    extendedDescription: "Properties appeal to buyers who value space, central access, cooler temperatures and a more local, settled atmosphere than the coastal regions. Curepipe and Forest Side benefit from proximity to Floréal, Vacoas, Phoenix, Quatre Bornes and the main central road network. Residents have access to supermarkets, clinics, pharmacies, schools, churches, sports clubs and the Metro Express, while the wider area remains well connected to Ebène, Moka and Port Louis. The climate is cooler and wetter than the coast, making it attractive to those who prefer fresh inland air and a more temperate daily environment. Buyers choose Curepipe - Forest Side for its established residential character, cooler climate, central convenience and appeal as a practical family base in the heart of Mauritius.",
    coordinates: "20° 19' 00\" S, 57° 31' 00\" E",
    latitude: -20.3167,
    longitude: 57.5167,
    climate: 'Cool Highland',
    priceRange: '$120k - $650k',
    highlights: ['Cooler Climate', 'Established Residential', 'Central Access'],
    heroImage: '',
    seo: {
      metaTitle: 'Curepipe - Forest Side Properties | Highland Mauritius',
      metaDescription: 'Properties in Curepipe - Forest Side, one of the coolest and most established residential areas in Mauritius. Highland living with central access.',
      keywords: ['Curepipe property', 'Forest Side real estate', 'highland Mauritius living'],
    },
  },

  'vacoas-phoenix': {
    slug: 'vacoas-phoenix',
    name: 'Vacoas - Phoenix',
    region: 'Central Mauritius',
    description: "Vacoas - Phoenix is one of Mauritius' most established central residential areas, offering a practical inland lifestyle with cooler weather, strong road access and proximity to schools, shopping centres, sports facilities and daily services. Located between Curepipe, Quatre Bornes, Floréal and the wider Plaines Wilhems corridor, the region combines long-standing neighbourhoods, family homes, apartment buildings and convenient urban amenities.",
    extendedDescription: "Properties appeal to buyers who value central access, residential stability and a more temperate climate than the coastal regions. Vacoas and Phoenix benefit from proximity to Phoenix Mall, Jumbo Phoenix, Gymkhana, local schools, clinics, supermarkets, pharmacies and the main road network connecting the centre to Ebène, Moka, Port Louis and the south. The area is also close to Curepipe and Floréal, giving residents access to additional retail, healthcare and education options within a short drive. Its cooler climate, established communities and practical infrastructure make it particularly suitable for families and professionals seeking everyday convenience in central Mauritius. Buyers choose Vacoas - Phoenix for its central position, cooler inland climate, strong residential character and convenient access to the island's main business, education and transport corridors.",
    coordinates: "20° 18' 00\" S, 57° 29' 30\" E",
    latitude: -20.3000,
    longitude: 57.4917,
    climate: 'Cool Plateau',
    priceRange: '$120k - $650k',
    highlights: ['Cooler Inland Living', 'Family Residential', 'Strong Road Access'],
    heroImage: '',
    seo: {
      metaTitle: 'Vacoas - Phoenix Properties | Central Mauritius',
      metaDescription: 'Properties in Vacoas - Phoenix, one of Mauritius most established central residential areas. Cooler climate, family living and strong road access.',
      keywords: ['Vacoas property', 'Phoenix real estate', 'central Mauritius family home'],
    },
  },
};

/**
 * Get area data by slug
 */
export function getAreaBySlug(slug: string): AreaData | undefined {
  return MAURITIUS_AREAS[slug];
}

/**
 * Get all areas in a region
 */
export function getAreasByRegion(region: CoastalRegion): AreaData[] {
  const regionGroup = MAURITIUS_REGIONS.find(r => r.region === region);
  if (!regionGroup) return [];
  
  return regionGroup.areas
    .map(area => MAURITIUS_AREAS[area.slug])
    .filter((area): area is AreaData => area !== undefined);
}

/**
 * Get all area slugs
 */
export function getAllAreaSlugs(): string[] {
  return Object.keys(MAURITIUS_AREAS);
}

/**
 * Get nearby areas (same region, excluding current)
 */
export function getNearbyAreas(slug: string): Array<{ slug: string; name: string; region: CoastalRegion }> {
  const area = MAURITIUS_AREAS[slug];
  if (!area) return [];
  
  const regionGroup = MAURITIUS_REGIONS.find(r => r.region === area.region);
  if (!regionGroup) return [];
  
  return regionGroup.areas
    .filter(a => a.slug !== slug)
    .map(a => ({ ...a, region: area.region }));
}
